import { compact, concat } from 'lodash/fp';

import { CONTENT_SECTIONS } from '../sections';
import { TILE_VARIANTS } from '../../../../ahm-constants/ui';
import claimTileImage from '../../../../images/claim-tile.svg';
import constants from '../../../../ahm-constants';
import getCommonQuestionsUrl from '../../utils/get-common-questions-url';
import helpTileImage from '../../../../images/help-tile.svg';
import homeOfferStripImage from './images/home-offer-strip.svg';
import insertFootnoteReference, { FOOTNOTE_PLACEHOLDER } from '../../../../utils/insert-footnote-reference';
import type { ContentData, Inclusion } from './types';

const EMERGENCY_HOME_ASSIST = {
  description: `This is a separate service provided to you by Allianz Global Assistance. Read the Emergency Home Assist [T&Cs](${constants.URLS.HELP.HOME_INSURANCE_FORMS_AND_GUIDES}).`,
  title: 'Emergency Home Assist',
};

const commonInclusions = (includePortables = false): Inclusion[] =>
  compact([
    {
      title: 'Escape of liquid',
    },
    {
      title: 'Riot, civil commotion or industrial unrest',
    },
    {
      title: 'Earthquake, tsunami or volcanic eruption',
    },
    {
      title: 'Fire, explosion or lightning',
    },
    {
      title: 'Accidental glass breakage',
    },
    {
      title: 'Storm and rainwater',
    },
    {
      title: 'Flood',
    },
    {
      title: 'Impact',
    },
    {
      title: 'Theft',
    },
    {
      title: 'Malicious damage',
    },
    {
      callout: 'comprehensive only',
      title: 'Accidental damage',
    },
    includePortables && {
      callout: 'optional',
      title: 'Portable valuables cover across Australia',
    },
  ]);

const homeOnlyBenefits = [
  {
    title: 'Emergency accommodation',
  },
  {
    title: 'Replacement locks',
  },
  {
    title: 'Damage to landscaping',
  },
];
const contentsOnlyBenefits = [
  {
    title: 'Visitor contents',
  },
  {
    title: 'Contents in the open air at home',
  },
  {
    title: 'Credit cards',
  },
];

const homeAndContentsBenefits = concat(
  [
    {
      title: 'Environmental benefits',
    },
    {
      title: 'Damage to landscaping',
    },
    {
      title: 'Emergency accommodation',
    },
  ],
  contentsOnlyBenefits,
);

const data: ContentData = {
  meta: {
    description:
      'Home and Contents insurance made simple. ahm has a range of affordable home and contents policies to suit your needs.',
    title: 'Home and contents insurance',
  },
  sections: [
    {
      type: CONTENT_SECTIONS.HEADER,
    },
    {
      type: CONTENT_SECTIONS.PROMOTIONS,
    },
    {
      footnote: {
        linkId: 'footnote-1',
        reference: '1',
      },
      image: {
        alt: 'piggy bank savings',
        src: homeOfferStripImage.src,
      },
      title: 'ahm and Medibank health members get up to 30% off!',
      type: CONTENT_SECTIONS.OFFER_STRIP,
    },
    {
      type: CONTENT_SECTIONS.VALUE_PROPOSITIONS,
    },
    {
      cta: {
        name: 'Start a quote',
        url: `${constants.URLS.SALES.HOME_INSURANCE}/quote`,
      },
      footnote: {
        linkId: 'footnote-2',
        reference: '2',
      },
      groups: [
        {
          addons: [EMERGENCY_HOME_ASSIST],
          benefits: homeOnlyBenefits,
          contentTitle: 'Home only cover',
          description: `What it says on the tin. This cover is for home owners needing protection for their house rather than the stuff inside. Plus, you’ll also get 10% off as an ahm or Medibank health member${FOOTNOTE_PLACEHOLDER}.`,
          descriptionFootnote: {
            linkId: 'footnote-1',
            reference: '1',
          },
          inclusions: commonInclusions(),
          tabTitle: 'Home only cover',
        },
        {
          addons: [EMERGENCY_HOME_ASSIST],
          benefits: homeAndContentsBenefits,
          contentTitle: 'Home and contents cover',
          description: `For home owners who need protection for their house and their stuff too. Plus, you’ll also get 10% off as an ahm or Medibank health member${FOOTNOTE_PLACEHOLDER}.`,
          descriptionFootnote: {
            linkId: 'footnote-1',
            reference: '1',
          },
          inclusions: commonInclusions(true),
          tabTitle: 'Home and contents cover',
        },
        {
          addons: [EMERGENCY_HOME_ASSIST],
          benefits: contentsOnlyBenefits,
          contentTitle: 'Contents only cover',
          description: `For people who just need cover for their stuff. Plus, you’ll also get up 10% off as an ahm or Medibank health member${FOOTNOTE_PLACEHOLDER}.`,
          descriptionFootnote: {
            linkId: 'footnote-1',
            reference: '1',
          },
          inclusions: commonInclusions(true),
          tabTitle: 'Contents only cover',
        },
      ],
      title: 'Choose cover that works for you',
      type: CONTENT_SECTIONS.INCLUSIONS,
    },
    {
      tiles: [
        [
          {
            ctas: [
              {
                name: 'Make a claim',
                url: `${constants.URLS.SALES.HOME_INSURANCE}/login`,
              },
            ],
            footnote: {
              linkId: 'footnote-4',
              reference: '4',
            },
            image: {
              alt: 'sign saing "Open 24/7 claims"',
              src: claimTileImage.src,
            },
            subtitle: insertFootnoteReference({
              footnote: {
                reference: '4',
              },
              text: `Most claims can be lodged online 24/7${FOOTNOTE_PLACEHOLDER}. In some cases, you’ll even get a decision within minutes.`,
            }),
            title: 'Easy claiming 24/7',
            variant: TILE_VARIANTS.VERTICAL_CENTER,
          },
          {
            ctas: [
              {
                name: 'More on home & contents insurance',
                url: getCommonQuestionsUrl('home'),
              },
            ],
            image: {
              alt: 'man screaming for help',
              src: helpTileImage.src,
            },
            subtitle:
              'Find out more about our home and contents insurance product, how to make a claim and how to manage your policy.',
            title: 'Need a quick answer?',
            variant: TILE_VARIANTS.VERTICAL_CENTER,
          },
        ],
      ],
      type: CONTENT_SECTIONS.TILES,
    },
    {
      type: CONTENT_SECTIONS.COMMON_QUESTIONS,
    },
    {
      type: CONTENT_SECTIONS.QUICK_LINKS,
    },
    {
      type: CONTENT_SECTIONS.TERMS_AND_CONDITIONS,
    },
  ],
  type: 'home',
};

export default data;
